import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Download, CheckCircle, RefreshCw, XCircle, AlertTriangle, Upload, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { AuthContext } from '../../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../../utils/config';

// Constants removed - API guide shows BankRecord model doesn't include status or type fields

const BankTransactions = () => {
    const { token } = useContext(AuthContext);
    const [bankRecords, setBankRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showImportModal, setShowImportModal] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState('');
    const [importSuccess, setImportSuccess] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);

    // Fetch bank records from API
    useEffect(() => {
        fetchBankRecords();
    }, [token]);

    const fetchBankRecords = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await fetch(API_ENDPOINTS.BANK_RECORDS_LIST, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized: Please login again');
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || `Failed to fetch bank records: ${response.statusText}`);
            }

            const data = await response.json();
            // Handle both array and paginated responses
            const records = Array.isArray(data) ? data : (data.results || data.data || []);
            setBankRecords(records);
        } catch (err) {
            setError(err.message || 'Failed to load bank transactions');
            console.error('Error fetching bank records:', err);
        } finally {
            setLoading(false);
        }
    };

    // Process and sort bank records according to API format
    const transactionsWithBalance = useMemo(() => {
        if (!Array.isArray(bankRecords)) return [];

        const sorted = [...bankRecords].sort((a, b) => {
            const da = new Date(a.date);
            const db = new Date(b.date);
            if (da.getTime() === db.getTime()) {
                return (a.id || 0) - (b.id || 0);
            }
            return da - db;
        });

        // API provides withdraw, deposit, and balance directly per API guide
        // Balance represents cumulative position at that transaction
        return sorted.map((tx) => {
            const withdraw = parseFloat(tx.withdraw || 0);
            const deposit = parseFloat(tx.deposit || 0);
            // Use balance from API (cumulative balance) as per API guide
            const balance = parseFloat(tx.balance || 0);
            
            return {
                ...tx,
                withdraw,
                deposit,
                runningBalance: balance // Use balance directly from API
            };
        });
    }, [bankRecords]);

    // Handle file selection for CSV import
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check both file extension and MIME type as per API guide
            const fileName = file.name.toLowerCase();
            const isValidExtension = fileName.endsWith('.csv');
            const isValidMimeType = file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || file.type === '';
            
            if (isValidExtension && isValidMimeType) {
            setSelectedFile(file);
            setImportError('');
            } else {
                setImportError('Please select a valid CSV file (.csv extension required)');
                setSelectedFile(null);
            }
        } else {
            setImportError('Please select a CSV file');
            setSelectedFile(null);
        }
    };

    // Convert date from DD-MM-YYYY to YYYY-MM-DD format
    const convertDateFormat = (dateString) => {
        if (!dateString || dateString.trim() === '') return dateString;
        
        // Try to parse DD-MM-YYYY format
        const ddmmyyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
        const match = dateString.trim().match(ddmmyyyyPattern);
        
        if (match) {
            const day = match[1].padStart(2, '0');
            const month = match[2].padStart(2, '0');
            const year = match[3];
            return `${year}-${month}-${day}`;
        }
        
        // If already in YYYY-MM-DD format, return as is
        const yyyymmddPattern = /^\d{4}-\d{2}-\d{2}$/;
        if (dateString.trim().match(yyyymmddPattern)) {
            return dateString.trim();
        }
        
        // Return original if format doesn't match
        return dateString;
    };

    // Parse CSV line handling quoted fields
    const parseCSVLine = (line) => {
        const columns = [];
        let currentColumn = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentColumn += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                columns.push(currentColumn);
                currentColumn = '';
            } else {
                currentColumn += char;
            }
        }
        columns.push(currentColumn); // Add last column
        return columns;
    };

    // Process CSV file to convert date formats
    const processCSVFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    // Handle both Windows (\r\n) and Unix (\n) line endings
                    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                    
                    if (lines.length === 0) {
                        reject(new Error('CSV file is empty'));
                        return;
                    }
                    
                    // Process each line
                    const processedLines = lines.map((line, index) => {
                        // First line is header, return as is (skip comment lines)
                        if (index === 0 && line.trim().startsWith('#')) {
                            return line; // Return comment line as is
                        }
                        if (index === 0 || (index === 1 && lines[0].trim().startsWith('#'))) {
                            return line; // Return header as is
                        }
                        
                        // Skip comment lines
                        if (line.trim().startsWith('#')) {
                            return line;
                        }
                        
                        // Parse CSV line
                        const columns = parseCSVLine(line);
                        
                        // Convert date in first column (Date column) - remove quotes if present
                        if (columns.length > 0) {
                            let dateValue = columns[0].trim();
                            // Remove surrounding quotes if present
                            if ((dateValue.startsWith('"') && dateValue.endsWith('"')) ||
                                (dateValue.startsWith("'") && dateValue.endsWith("'"))) {
                                dateValue = dateValue.slice(1, -1);
                            }
                            const convertedDate = convertDateFormat(dateValue);
                            columns[0] = convertedDate;
                        }
                        
                        // Reconstruct line with proper quoting if needed
                        return columns.map(col => {
                            // If column contains comma, quote, or newline, wrap in quotes
                            if (col.includes(',') || col.includes('"') || col.includes('\n')) {
                                return `"${col.replace(/"/g, '""')}"`;
                            }
                            return col;
                        }).join(',');
                    });
                    
                    // Create new CSV content
                    const processedCSV = processedLines.join('\n');
                    
                    // Create new blob with processed CSV
                    const blob = new Blob([processedCSV], { type: 'text/csv;charset=utf-8' });
                    const processedFile = new File([blob], file.name, { type: 'text/csv' });
                    
                    resolve(processedFile);
                } catch (error) {
                    reject(new Error(`Failed to process CSV file: ${error.message}`));
                }
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to read CSV file'));
            };
            
            reader.readAsText(file, 'UTF-8');
        });
    };

    // Handle CSV import submission
    const handleImportSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            setImportError('Please select a CSV file');
            return;
        }

        try {
            setImportLoading(true);
            setImportError('');
            setImportSuccess(null);

            // Process CSV file to convert date formats
            let fileToUpload = selectedFile;
            try {
                fileToUpload = await processCSVFile(selectedFile);
            } catch (processError) {
                console.warn('Could not process CSV file, uploading original:', processError.message);
                // Continue with original file if processing fails
            }

            const formData = new FormData();
            formData.append('file', fileToUpload);

            const response = await fetch(API_ENDPOINTS.BANK_IMPORT, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized: Please login again');
                }
                const errorData = await response.json().catch(() => ({}));
                // API guide specifies error field for error responses
                throw new Error(errorData.error || errorData.message || `Import failed: ${response.statusText}`);
            }

            const result = await response.json();
            // API guide specifies: status, records_created, records_failed, errors
            setImportSuccess({
                status: result.status || 'Import completed',
                recordsCreated: result.records_created || 0,
                recordsFailed: result.records_failed || 0,
                errors: result.errors || []
            });

            // Refresh bank records after successful import
            if (result.records_created > 0) {
                await fetchBankRecords();
            }

            // Reset file input
            setSelectedFile(null);
            // Reset file input element
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) {
                fileInput.value = '';
            }

            // Close modal after 2 seconds if successful
            setTimeout(() => {
                setShowImportModal(false);
            }, 2000);
        } catch (err) {
            setImportError(err.message || 'Failed to import CSV file');
            console.error('Error importing CSV:', err);
        } finally {
            setImportLoading(false);
        }
    };

    // Reset import modal
    const resetImportModal = () => {
        setShowImportModal(false);
        setSelectedFile(null);
        setImportError('');
        setImportSuccess(null);
        // Reset file input element
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (typeof amount !== 'number') return '₹0';
        return `₹${Math.abs(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        })}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return dateString;
        }
    };

    // Format datetime for Created At column
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-IN', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Handle export to Excel
    const handleExport = async () => {
        try {
            setExportLoading(true);
            setError('');

            // Use the export endpoint: https://backend-api-itt1.onrender.com/api/banking/export/
            const exportUrl = API_ENDPOINTS.BANK_EXPORT;

            const response = await fetch(exportUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized: Please login again');
                }
                if (response.status === 404) {
                    throw new Error('Export endpoint not found. Please contact your administrator.');
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || `Export failed: ${response.status} ${response.statusText}`);
            }

            // Get the content type from response headers
            const contentType = response.headers.get('content-type') || '';

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'bank_records_export.xlsx';

            if (contentDisposition) {
                // Try multiple patterns to extract filename
                const patterns = [
                    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
                    /filename\*?=['"]?([^'";\n]+)['"]?/,
                    /filename=([^;\n]+)/
                ];
                
                for (const pattern of patterns) {
                    const match = contentDisposition.match(pattern);
                    if (match && match[1]) {
                        filename = decodeURIComponent(match[1].replace(/['"]/g, ''));
                        break;
                    }
                }
            }

            // Ensure filename has correct extension based on content type
            if (contentType.includes('excel') || contentType.includes('spreadsheet') || contentType.includes('xlsx')) {
                if (!filename.endsWith('.xlsx')) {
                    filename = filename.replace(/\.[^.]*$/, '') + '.xlsx';
                }
            } else if (contentType.includes('csv')) {
                if (!filename.endsWith('.csv')) {
                    filename = filename.replace(/\.[^.]*$/, '') + '.csv';
                }
            }

            // Get the blob data
            const blobData = await response.blob();
            
            // Check if the response is actually an Excel file by checking the first bytes
            // Excel files (xlsx) start with PK (ZIP file signature)
            const firstBytes = await blobData.slice(0, 4).arrayBuffer();
            const uint8Array = new Uint8Array(firstBytes);
            const isExcelFile = uint8Array[0] === 0x50 && uint8Array[1] === 0x4B; // PK signature
            
            // Determine the correct MIME type
            let blobType = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            
            // If content type is not Excel, but filename is .xlsx or file signature matches, use Excel MIME type
            if ((filename.endsWith('.xlsx') || isExcelFile) && !blobType.includes('spreadsheet') && !blobType.includes('excel')) {
                blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            }

            // Create blob with correct MIME type
            const blob = new Blob([blobData], { type: blobType });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            setError(err.message || 'Failed to export bank records');
            console.error('Error exporting bank records:', err);
        } finally {
            setExportLoading(false);
        }
    };

    // Handle template download - creates empty template with headers only
    const handleDownloadTemplate = async () => {
        try {
            setTemplateLoading(true);
            setError('');

            // Create empty CSV template with headers only (no mock data)
            // According to API guide: Date, Description, Reference, Withdraw, Deposit, Balance
            // Date format must be YYYY-MM-DD
            const csvHeaders = 'Date,Description,Reference,Withdraw,Deposit,Balance';
            const csvNote = '# Date format: YYYY-MM-DD (e.g., 2024-01-15)';
            const csvContent = csvNote + '\n' + csvHeaders + '\n'; // Note + headers, no data rows
            
            // Create blob with CSV content
            const blob = new Blob([csvContent], { 
                type: 'text/csv;charset=utf-8;' 
            });
            
            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
            const filename = `bank_records_template_${timestamp}.csv`;

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            setError(err.message || 'Failed to download template');
            console.error('Error downloading template:', err);
        } finally {
            setTemplateLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Bank Transactions</h1>
                    <p className="text-muted-foreground">Track and manage all bank transactions</p>
                </div>
                <div className="flex items-center space-x-2">
                    <Button variant="outline" onClick={fetchBankRecords} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleExport}
                        disabled={exportLoading || bankRecords.length === 0}
                        title={bankRecords.length === 0 ? 'No records to export' : 'Export bank records to Excel'}
                    >
                        <Download className={`w-4 h-4 mr-2 ${exportLoading ? 'animate-spin' : ''}`} />
                        Export Excel
                    </Button>
                    <Button variant="outline" onClick={() => setShowImportModal(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Report
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleDownloadTemplate}
                        disabled={templateLoading}
                        title="Download CSV template file for importing bank records"
                    >
                        <Download className={`w-4 h-4 mr-2 ${templateLoading ? 'animate-spin' : ''}`} />
                        Download Template
                    </Button>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-red-900">{error}</p>
                        <button
                            onClick={() => setError('')}
                            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && !bankRecords.length ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 flex items-center justify-center">
                    <div className="text-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">Loading bank transactions...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Bank Ledger Table */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Reference
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Withdraw
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Deposit
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Balance
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created By
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created At
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {transactionsWithBalance.length > 0 ? (
                                        transactionsWithBalance.map((tx) => {
                                            return (
                                                <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {formatDate(tx.date)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                                        {tx.description || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                                        {tx.reference || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                                                        {tx.withdraw > 0 ? formatCurrency(tx.withdraw) : '—'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                                                        {tx.deposit > 0 ? formatCurrency(tx.deposit) : '—'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                                                        {formatCurrency(tx.runningBalance)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                        {tx.created_by ? (typeof tx.created_by === 'string' ? tx.created_by : tx.created_by.username || tx.created_by.email || '-') : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                        {tx.created_at ? formatDateTime(tx.created_at) : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                                <p>No bank transactions found. Start by importing a CSV file.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Record Count */}
                    {transactionsWithBalance.length > 0 && (
                        <div className="text-sm text-gray-600 flex items-center justify-between">
                            <span>Showing {transactionsWithBalance.length} transaction(s)</span>
                            <span>
                                Total Balance:{' '}
                                <span className="font-semibold text-gray-900">
                                    {formatCurrency(
                                        transactionsWithBalance[transactionsWithBalance.length - 1]?.runningBalance || 0
                                    )}
                                </span>
                            </span>
                        </div>
                    )}
                </>
            )}

            {/* CSV Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-lg font-semibold">Import Bank Transactions</h2>
                            <button
                                onClick={resetImportModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            {importSuccess ? (
                                // Success State
                                <div className="text-center">
                                    <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Import Successful!</h3>
                                    <p className="text-sm text-gray-600 mb-2">
                                        <strong>{importSuccess.recordsCreated}</strong> record(s) imported successfully.
                                    </p>
                                    {importSuccess.recordsFailed > 0 && (
                                        <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                                            <p className="text-sm text-yellow-800 font-medium mb-2">
                                                <strong>{importSuccess.recordsFailed}</strong> record(s) failed to import.
                                            </p>
                                            {importSuccess.errors && importSuccess.errors.length > 0 && (
                                                <div className="mt-2 text-xs text-yellow-700 max-h-32 overflow-y-auto space-y-1">
                                                    {importSuccess.errors.map((err, idx) => (
                                                        <p key={idx} className="mb-1">
                                                            • {err}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {importSuccess.status && (
                                        <p className="text-xs text-gray-500 italic">
                                            Status: {importSuccess.status}
                                        </p>
                                    )}
                                    <Button onClick={resetImportModal} className="w-full">
                                        Close
                                    </Button>
                                </div>
                            ) : (
                                // Upload Form
                                <form onSubmit={handleImportSubmit} className="space-y-4">
                                    {/* File Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            CSV File
                                        </label>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleFileChange}
                                            className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            CSV Format: Date, Description, Reference, Withdraw, Deposit, Balance
                                        </p>
                                        <p className="mt-1 text-xs text-blue-600 font-medium">
                                            ⚠️ Date format must be YYYY-MM-DD (e.g., 2024-01-15). Dates in DD-MM-YYYY format will be automatically converted.
                                        </p>
                                    </div>

                                    {/* Error Message */}
                                    {importError && (
                                        <div className="p-3 bg-red-50 rounded-lg flex items-start gap-2">
                                            <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                            <p className="text-sm text-red-700">{importError}</p>
                                        </div>
                                    )}

                                    {/* Selected File Display */}
                                    {selectedFile && (
                                        <div className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-blue-900">{selectedFile.name}</p>
                                                <p className="text-xs text-blue-700">
                                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                                </p>
                                            </div>
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        </div>
                                    )}

                                    {/* Buttons */}
                                    <div className="flex gap-2 pt-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={resetImportModal}
                                            disabled={importLoading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={!selectedFile || importLoading}
                                            className="flex-1"
                                        >
                                            {importLoading ? (
                                                <>
                                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                    Importing...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-4 h-4 mr-2" />
                                                    Import
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankTransactions;
